// src/pages/ProductosPage.tsx
import { useEffect, useState } from 'react'
import api from '../services/api'
import { Plus, Search, Edit2, Trash2, Package, X, RotateCcw } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

// Categorías de productos para accesorios de autos
const CATEGORIAS = [
  { value: '', label: 'Todas las categorías' },
  { value: 'ACEITES_LUBRICANTES', label: '🛢️ Aceites y Lubricantes' },
  { value: 'FILTROS', label: '🔧 Filtros' },
  { value: 'FRENOS', label: '🛑 Frenos' },
  { value: 'SUSPENSION', label: '⚙️ Suspensión' },
  { value: 'ILUMINACION', label: '💡 Iluminación' },
  { value: 'ELECTRICOS', label: '🔌 Eléctricos' },
  { value: 'NEUMATICOS', label: '🚗 Neumáticos' },
  { value: 'BATERIAS', label: '🔋 Baterías' },
  { value: 'CARROCERIA', label: '🚙 Carrocería' },
  { value: 'MOTOR', label: '🏎️ Motor' },
  { value: 'LIMPIEZA_AUTO', label: '🧽 Limpieza Auto' },
  { value: 'ACCESORIOS', label: '✨ Accesorios' },
  { value: 'OTROS', label: '📦 Otros' }
]

interface Producto {
  id_producto: number
  detalle: string
  codigo_barras: string
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  costo_produccion: number
  cantidad: number
  iva: string
  departamento?: string
  descontinuado?: number
}

interface FormProducto {
  detalle: string
  codigo_barras: string
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  costo_produccion: number
  cantidad: number
  iva: string
  departamento: string
}

const initialForm: FormProducto = {
  detalle: '',
  codigo_barras: '',
  precio_venta_a: 0,
  precio_venta_b: 0,
  precio_venta_c: 0,
  costo_produccion: 0,
  cantidad: 0,
  iva: '12',
  departamento: 'OTROS'
}

export function ProductosPage() {
  const user = useAuthStore(state => state.user)
  const isAdmin = user?.rol === 'ADMINISTRADOR'
  const [productos, setProductos] = useState<Producto[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState('')
  const [incluirDescontinuados, setIncluirDescontinuados] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormProducto>(initialForm)
  const [saving, setSaving] = useState(false)

  const fetchProductos = async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/productos?search=${search}&incluirDescontinuados=${incluirDescontinuados}`)
      let prods = data.data || []
      // Filtrar por categoría si está seleccionada
      if (categoriaFiltro) {
        prods = prods.filter((p: Producto) => p.departamento === categoriaFiltro)
      }
      setProductos(prods)
    } catch (error) {
      console.error('Error cargando productos:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProductos()
  }, [search, categoriaFiltro, incluirDescontinuados])

  const handleOpenModal = (producto?: Producto) => {
    if (producto) {
      setEditingId(producto.id_producto)
      setForm({
        detalle: producto.detalle,
        codigo_barras: producto.codigo_barras || '',
        precio_venta_a: producto.precio_venta_a,
        precio_venta_b: producto.precio_venta_b,
        precio_venta_c: producto.precio_venta_c,
        costo_produccion: producto.costo_produccion || 0,
        cantidad: producto.cantidad,
        iva: producto.iva,
        departamento: producto.departamento || 'OTROS'
      })
    } else {
      setEditingId(null)
      setForm(initialForm)
    }
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingId(null)
    setForm(initialForm)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.detalle.trim()) {
      alert('El nombre del producto es requerido')
      return
    }

    setSaving(true)
    try {
      if (editingId) {
        await api.put(`/productos/${editingId}`, form)
      } else {
        await api.post('/productos', form)
      }
      handleCloseModal()
      fetchProductos()
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } }
        alert(axiosError.response?.data?.error || 'Error al guardar producto')
      } else {
        alert('Error al guardar producto')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar/descontinuar este producto?')) return

    try {
      const { data } = await api.delete(`/productos/${id}`)
      if (data.descontinuado) {
        alert('Producto marcado como DESCONTINUADO (ya no se vende)')
      } else {
        alert('Producto eliminado exitosamente')
      }
      fetchProductos()
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } }
        alert(axiosError.response?.data?.error || 'Error al eliminar producto')
      } else {
        alert('Error al eliminar producto')
      }
    }
  }

  const handleReactivar = async (id: number) => {
    if (!confirm('¿Desea reactivar este producto para que se pueda vender nuevamente?')) return

    try {
      await api.patch(`/productos/${id}/reactivar`)
      alert('Producto reactivado exitosamente')
      fetchProductos()
    } catch (error) {
      alert('Error al reactivar producto')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Productos</h1>
        {isAdmin && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Producto
          </button>
        )}
      </div>

      {/* Búsqueda y Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 min-w-[200px]"
          >
            {CATEGORIAS.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          {isAdmin && (
            <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={incluirDescontinuados}
                onChange={(e) => setIncluirDescontinuados(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm">Ver descontinuados</span>
            </label>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : productos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Package className="w-12 h-12 mb-4" />
            <p>No hay productos registrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Mayorista</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">IVA</th>
                  {isAdmin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {productos.map((producto) => (
                  <tr key={producto.id_producto} className={`hover:bg-gray-50 ${producto.descontinuado ? 'bg-red-50 opacity-75' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium">{producto.detalle}</div>
                      <div className="text-xs text-gray-400">{producto.codigo_barras || 'Sin código'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{CATEGORIAS.find(c => c.value === producto.departamento)?.label || '📦 Otros'}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {producto.descontinuado ? (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          🚫 DESCONTINUADO
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          ✅ ACTIVO
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">${producto.precio_venta_a.toFixed(2)}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        producto.cantidad <= 0
                          ? 'bg-red-100 text-red-700'
                          : producto.cantidad <= 10
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {producto.cantidad}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">{producto.iva}%</td>
                    {isAdmin && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {producto.descontinuado ? (
                            <button
                              onClick={() => handleReactivar(producto.id_producto)}
                              className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                              title="Reactivar producto"
                            >
                              <RotateCcw className="w-5 h-5" />
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleOpenModal(producto)}
                                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded"
                              >
                                <Edit2 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(producto.id_producto)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCloseModal} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editingId ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button onClick={handleCloseModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={form.detalle}
                  onChange={(e) => setForm({ ...form, detalle: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código de Barras</label>
                <input
                  type="text"
                  value={form.codigo_barras}
                  onChange={(e) => setForm({ ...form, codigo_barras: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🏭 Costo de Producción
                  <span className="text-xs text-gray-400 block">Costo para calcular márgenes</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.costo_produccion}
                  onChange={(e) => setForm({ ...form, costo_produccion: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    💰 Mayorista
                    <span className="text-xs text-gray-400 block">Precio para distribuidores</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.precio_venta_a}
                    onChange={(e) => setForm({ ...form, precio_venta_a: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🏪 Minorista
                    <span className="text-xs text-gray-400 block">Precio sugerido tiendas</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.precio_venta_b}
                    onChange={(e) => setForm({ ...form, precio_venta_b: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    👤 Público
                    <span className="text-xs text-gray-400 block">Precio cliente final</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.precio_venta_c}
                    onChange={(e) => setForm({ ...form, precio_venta_c: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                  <input
                    type="number"
                    value={form.cantidad}
                    onChange={(e) => setForm({ ...form, cantidad: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IVA (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    list="iva-options"
                    value={form.iva}
                    onChange={(e) => setForm({ ...form, iva: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Ej: 0, 12, 15"
                  />
                  <datalist id="iva-options">
                    <option value="0">0%</option>
                    <option value="12">12%</option>
                    <option value="15">15%</option>
                  </datalist>
                  <p className="text-xs text-gray-400 mt-1">Opciones comunes: 0%, 12%, 15%</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                  <select
                    value={form.departamento}
                    onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {CATEGORIAS.filter(c => c.value).map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
